import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { ANSWER_POSTS, ANSWER_TRADE_LABEL, answersByTrade } from "@/lib/answers-data";
import { BUSINESS } from "@/lib/site-data";

const BASE_URL = "https://lehitradessaeo.com";
const PAGE_SIZE = 24;

const searchSchema = z.object({
  page: fallback(z.number().int().min(1), 1).default(1),
  trade: fallback(z.string(), "").default(""),
});

function canonicalFor(page: number, trade: string) {
  const qs: string[] = [];
  if (trade) qs.push(`trade=${trade}`);
  if (page > 1) qs.push(`page=${page}`);
  return qs.length ? `${BASE_URL}/answers?${qs.join("&")}` : `${BASE_URL}/answers`;
}

export const Route = createFileRoute("/answers")({
  validateSearch: zodValidator(searchSchema),
  head: ({ match }) => {
    const s = (match.search as { page?: number; trade?: string } | undefined) ?? {};
    const page = s.page ?? 1;
    const trade = s.trade ?? "";
    const label = trade ? ANSWER_TRADE_LABEL[trade] : "";
    const title = trade
      ? `${label} SEO Answers${page > 1 ? ` — Page ${page}` : ""} | ${BUSINESS.shortName}`
      : `Trades SEO Answers Library${page > 1 ? ` — Page ${page}` : ""} | ${BUSINESS.shortName}`;
    const desc = trade
      ? `Straight answers to the SEO, Google Ads, and lead-gen questions ${label.toLowerCase()} actually ask.`
      : "500+ answer-first posts covering SEO, Google Ads, Map Pack, LSAs, GBP, and lead-gen for 25 trades.";
    const canonical = canonicalFor(page, trade);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "robots", content: "index, follow" },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: AnswersIndex,
});

function AnswersIndex() {
  const { page, trade } = Route.useSearch();
  const grouped = answersByTrade();
  const all = trade ? (grouped[trade] ?? []) : ANSWER_POSTS;
  const totalPages = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = all.slice(start, start + PAGE_SIZE);
  const label = trade ? ANSWER_TRADE_LABEL[trade] : "";

  return (
    <main className="mx-auto max-w-6xl px-6 py-20">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        Answers Library
      </p>
      <h1 className="mt-3 font-display text-5xl tracking-tight md:text-6xl">
        {trade ? `${label} SEO answers` : "Every trades SEO question, answered"}
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
        {all.length} answer-first posts covering the SEO, Google Ads, GBP, LSA, and lead-gen questions
        {trade ? ` ${label.toLowerCase()} ` : " trades contractors "}
        actually ask.
      </p>

      <nav aria-label="Filter by trade" className="mt-8 flex flex-wrap gap-2">
        <Link
          to="/answers"
          search={{ page: 1, trade: "" }}
          className={`rounded-full border px-3 py-1 text-xs ${!trade ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
        >
          All trades
        </Link>
        {Object.keys(grouped).sort().map((t) => (
          <Link
            key={t}
            to="/answers"
            search={{ page: 1, trade: t }}
            className={`rounded-full border px-3 py-1 text-xs ${trade === t ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            {ANSWER_TRADE_LABEL[t] ?? t}
          </Link>
        ))}
      </nav>

      {pageItems.length === 0 ? (
        <p className="mt-16 text-muted-foreground">No posts yet for this filter.</p>
      ) : (
        <ul className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pageItems.map((p) => (
            <li key={p.slug} className="rounded-xl border border-border bg-card p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {ANSWER_TRADE_LABEL[p.tradeSlug] ?? p.tradeSlug} · {p.readMinutes} min
              </p>
              <h2 className="mt-2 font-display text-lg tracking-tight">
                <Link to="/answers/$slug" params={{ slug: p.slug }} className="hover:text-primary">
                  {p.title}
                </Link>
              </h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
            </li>
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <nav className="mt-12 flex items-center justify-between" aria-label="Pagination">
          {page > 1 ? (
            <Link to="/answers" search={{ page: page - 1, trade }} rel="prev" className="text-sm font-medium text-primary hover:underline">
              ← Previous
            </Link>
          ) : <span />}
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          {page < totalPages ? (
            <Link to="/answers" search={{ page: page + 1, trade }} rel="next" className="text-sm font-medium text-primary hover:underline">
              Next →
            </Link>
          ) : <span />}
        </nav>
      )}
    </main>
  );
}